<template>
  <v-dialog v-model="dialog" max-width="500" persistent>
    <v-card>
      <v-sheet class="px-6 d-flex justify-space-between align-center" color="bg1">
        <v-card-title class="py-3 px-0">
          <v-icon class="mr-2">{{ dialogIcon }}</v-icon>
          <span>{{ dialogTitle }}</span>
        </v-card-title>
      </v-sheet>

      <v-card-text class="pt-6">
        <v-form ref="form" v-model="valid" lazy-validation>
          <!-- Parent breadcrumb (when creating inside a folder) -->
          <div v-if="parentProject && !isEditMode" class="mb-4 d-flex align-center text-caption grey--text">
            <v-icon x-small class="mr-1">mdi-folder</v-icon>
            <span>Inside: <strong>{{ parentBreadcrumb }}</strong></span>
          </div>

          <v-text-field
            v-model="formData.name"
            :label="nameLabel"
            outlined
            dense
            :rules="nameRules"
            v-virtual-keyboard
            required
            autofocus
            @keyup.enter="save">
          </v-text-field>
        </v-form>
      </v-card-text>

      <v-card-actions style="background-color: var(--v-bg1-base)">
        <v-btn text color="error" @click="cancel">
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="save" :disabled="!valid">
          {{ isEditMode ? 'Save' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ProjectDialog',
  data() {
    return {
      dialog: false,
      valid: false,
      isEditMode: false,
      // 'folder' = container that holds sub-folders/projects, 'project' = leaf with images
      createType: 'project',
      formData: {
        name: '',
      },
      editingProject: null,
      parentProject: null,
      existingProjects: [],
      nameRules: [
        v => !!v || 'Name is required',
        v => (v && v.trim().length > 0) || 'Name cannot be empty',
        v => this.validateUniqueName(v) || 'An item with this name already exists here',
      ],
    };
  },
  computed: {
    dialogTitle() {
      return this.isEditMode ? 'Edit Project' : 'Add Project';
    },
    dialogIcon() {
      return this.isEditMode ? 'mdi-pencil' : 'mdi-folder-plus';
    },
    nameLabel() {
      return 'Project Name';
    },
    parentBreadcrumb() {
      if (!this.parentProject) return '';
      return this.parentProject.name;
    },
  },
  methods: {
    /**
     * Open dialog to create or edit a project/folder.
     * @param {Array} projects - sibling projects (for uniqueness validation)
     * @param {Object|null} project - project to edit (null = create mode)
     * @param {Object|null} options - { parentProject, createType }
     */
    open(projects, project = null, options = {}) {
      this.existingProjects = projects || [];
      this.isEditMode = !!project;
      this.editingProject = project;
      this.parentProject = options.parentProject || null;
      this.createType = options.createType || 'project';

      if (project) {
        this.formData.name = project.name;
      } else {
        this.formData.name = '';
      }

      this.dialog = true;
      this.$nextTick(() => {
        this.$refs.form?.resetValidation();
      });
    },

    validateUniqueName(name) {
      if (!name) return true;
      const trimmedName = name.trim();

      return !this.existingProjects.some(p => {
        const isSameProject = this.isEditMode && p.id === this.editingProject?.id;
        if (isSameProject) return false;
        return p.name === trimmedName;
      });
    },

    save() {
      if (this.$refs.form.validate()) {
        const trimmedName = this.formData.name.trim();
        const projectId = this.editingProject?.id || null;

        const payload = {
          id: projectId,
          name: trimmedName,
          parentId: this.parentProject?.id || this.editingProject?.parentId || null,
          hasImages: this.isEditMode
            ? this.editingProject.hasImages
            : this.createType !== 'folder',
        };
        this.$emit('save', payload);

        this.dialog = false;
      }
    },

    cancel() {
      this.dialog = false;
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped>
</style>
